import React, { useEffect, useState } from "react";
import Modal from "../shared/Modal";
import "./ModalEmailVerify.css";
import ActionButton from "../common/ActionButton";
import {
  apiAuthSendVerificationCode,
  apiAuthVerifyCode,
} from "../../services/AuthService";
import toast from "react-hot-toast";
import { toastFail } from "../../config";
import { Button } from "../../ds2/components";

interface ApiResponse {
  error?: boolean;
  data?: any;
}

interface ModalEmailVerifyProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
}

export const ModalEmailVerify: React.FC<ModalEmailVerifyProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [internalValue, setInternalValue] = useState<string>("");
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const validateEmail = (email: string): boolean => {
    const invalidDomains = [
      "gmail.com",
      "yahoo.com",
      "outlook.com",
      "hotmail.com",
      "icloud.com",
      "aol.com",
      "protonmail.com",
      "gmx.com",
      "mail.com",
      "yandex.com",
      "live.com",
      "me.com",
      "msn.com",
      "rediffmail.com",
      "mailinator.com",
      "tempmail.com",
      "10minutemail.com",
      "guerrillamail.com",
      "trashmail.com",
      "yopmail.com",
      "sharklasers.com",
      "getnada.com",
      "burnermail.io",
      "fakemailgenerator.com",
      "throwawaymail.com",
    ];

    const validEmail = email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    const domain = email.split("@")[1]?.toLowerCase();

    if (!validEmail) {
      return false;
    }

    if (domain.startsWith("zoho.com") && !domain.includes(".")) {
      toast("Please use your business Zoho domain", toastFail);
      return false;
    }

    if (invalidDomains.includes(domain)) {
      toast("Please use your business email address", toastFail);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!isVerifyingCode && internalValue) {
      setIsEmailValid(validateEmail(internalValue));
    }
  }, [internalValue, isVerifyingCode]);

  const handleEmailSubmit = async () => {
    if (!internalValue) return;

    try {
      const isValid = validateEmail(internalValue);
      if (!isValid) {
        return;
      }

      // Send verification code
      const response = (await apiAuthSendVerificationCode(
        internalValue
      )) as ApiResponse;
      if (response?.error) {
        toast("Failed to send verification code", toastFail);
        return;
      }

      // Switch to code verification mode
      setEmailValue(internalValue);
      setIsVerifyingCode(true);
      setInternalValue("");
    } catch (error) {
      console.error("Error during email verification:", error);
      toast("Failed to process email verification", toastFail);
    }
  };

  const handleCodeSubmit = async () => {
    if (!internalValue) return;

    try {
      const response = await apiAuthVerifyCode(emailValue, internalValue);
      console.log("response", response);
      if (!response) {
        toast("Invalid verification code", toastFail);
        return;
      }

      // Code verified successfully
      onSubmit(emailValue);
      onClose();
    } catch (error) {
      console.error("Error during code verification:", error);
      toast("Failed to verify code", toastFail);
    }
  };

  const handleSubmit = () => {
    if (isVerifyingCode) {
      handleCodeSubmit();
    } else {
      handleEmailSubmit();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "500px" }}
      canClose={false}
      escapeToClose={false}
      allowClickout={false}>
      <div className="modal-email-verify">
        <div className="modal-email-verify-title">
          <h2>
            {isVerifyingCode ? "Enter Verification Code" : "Enter your email"}
          </h2>
        </div>
        <div className="modal-email-verify-body">
          {!isVerifyingCode && (
            <div className="modal-email-verify-label">
              In order to continue using the Winslow Policy Studio, please enter
              your email. We will send a verification code to this email.
            </div>
          )}
          {isVerifyingCode && (
            <div className="modal-email-verify-label">
              Please enter the verification code sent to {emailValue}
            </div>
          )}
          <div className="modal-email-verify-content">
            <input
              type={isVerifyingCode ? "text" : "email"}
              value={internalValue}
              onChange={(e) => setInternalValue(e.target.value)}
              placeholder={
                isVerifyingCode ? "Enter verification code" : "Enter your email"
              }
            />
          </div>
        </div>
        <div className="modal-email-verify-buttons">
          {isVerifyingCode && (
            <Button
              label={"Go Back"}
              variant="secondary"
              onClick={() => {
                setIsVerifyingCode(false);
              }}
              disabled={!isVerifyingCode && !isEmailValid}
            />
          )}
          <Button
            label={isVerifyingCode ? "Verify Code" : "Submit"}
            variant="tertiary"
            onClick={handleSubmit}
            disabled={!isVerifyingCode && !isEmailValid}
          />
        </div>
      </div>
    </Modal>
  );
};
